import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { fetchAllBusinessClients } from '~/api';
import { useBoolean } from '~/hooks/useBoolean';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { IFormData } from '~/store/slices/formSlice';
import { useSelector } from '~/store';
import { selectBusinessUuid } from '~/store/slices/formSlice/selectors';
import { useUserLimits } from '~/hooks/useUserLimits';
import { BILLING_ROUTE } from '~/constants/routes';

export interface ICustomer {
  business_client_uuid: string;
  first_name: string;
  last_name: string;
  company_name: string;
  email: string;
  country_code: string;
  phone: string;
  currency: string;
  country: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  postal_code: string;
  state: string;
}

export default function useCustomerSection(
  setValue: (newValues: Partial<IFormData>) => void,
  selectedClient: string | null,
) {
  const [isLoading, loadingActions] = useBoolean();
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [isModalOpen, modalActions] = useBoolean();
  const businessUuid = useSelector(selectBusinessUuid);
  const { canCreateClient } = useUserLimits();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');

  function setCustomer(customer: ICustomer) {
    setValue({
      client_uuid: customer.business_client_uuid,
      currency: customer.currency,
    });
    setOpen(false);
  }

  async function fetchCustomers() {
    loadingActions.on();
    const promise = fetchAllBusinessClients(businessUuid);
    const response = await apiResponseHandler<Array<ICustomer>>(promise);

    if (response.type === ApiResponseEnum.Success) {
      setCustomers(response.data);

      if (clientId && !selectedClient) {
        const foundClient = response.data.find(
          customer => customer.business_client_uuid === clientId,
        );
        if (foundClient) {
          setCustomer(foundClient);
        }
      }
    } else {
      toast.error('Error fetching clients!');
    }

    loadingActions.off();
  }

  function onCloseClientModal() {
    modalActions.off();
  }

  function onOpenClientModal() {
    if (!canCreateClient && selectedClient === null) {
      // Case when trying to create a new client but user has reached the limit
      navigate(`${BILLING_ROUTE}?ref=clients`);
    }
    modalActions.on();
  }

  useEffect(() => {
    fetchCustomers();
  }, [isModalOpen]);

  return {
    isLoading,
    open,
    setOpen,
    customers,
    isModalOpen,
    onCloseClientModal,
    onOpenClientModal,
    setCustomer,
  };
}
